import React, { useState } from 'react';
import { Card, Row, Col, Badge, Button, Form, Table } from 'react-bootstrap';
import { useQuery } from 'react-query';
import { LICHHOCTHUCHANH_API, DONVI_API, NGANHDAOTAO_API } from '../../../utils/apiUrl';
import { Get } from '../../../utils/httpProvider';
import { toast } from 'react-toastify';

const AdminMatrixView = ({ contractId }) => {
    const [matrixViewMode, setMatrixViewMode] = useState('department'); // 'department' | 'student' | 'instructor'
    const [donViId, setDonViId] = useState('');
    const [nganhId, setNganhId] = useState('');
    const [search, setSearch] = useState('');

    // Fetch master data for dropdowns
    const { data: donVisData } = useQuery('matrixDonVis', () =>
        Get(`${DONVI_API.GET_PAGING}?PageIndex=1&PageSize=2000`).then(res => res?.data?.data || res?.data || res || [])
    );
    const { data: nganhsData } = useQuery('matrixNganhs', () =>
        Get(`${NGANHDAOTAO_API.GET_PAGING}?PageIndex=1&PageSize=2000`).then(res => res?.data?.data || res?.data || res || [])
    );

    // Fetch the matrix data with queries
    const { data: matrixData, isLoading } = useQuery(
        ['adminMatrix', contractId, donViId, nganhId, search],
        () => {
            let url = `${LICHHOCTHUCHANH_API.GET_ADMIN_MATRIX}/${contractId}`;
            const params = [];
            if (donViId) params.push(`donViId=${donViId}`);
            if (nganhId) params.push(`nganhDaoTaoId=${nganhId}`);
            if (search) params.push(`search=${encodeURIComponent(search)}`);
            if (params.length > 0) {
                url += `?${params.join('&')}`;
            }
            return Get(url).then(res => res?.data || res);
        },
        { enabled: !!contractId }
    );

    const handleExportMatrixCsv = () => {
        const weeks = matrixData?.weeks || [];
        const rows = matrixData?.rows || [];
        if (rows.length === 0) {
            toast.info("Không có dữ liệu ma trận để xuất.");
            return;
        }

        let csv = "\uFEFFKhoa/Trung tâm;" + weeks.map(w => w.rangeStr).join(';') + "\n";
        rows.forEach(r => {
            csv += `"${r.donViTen}";`;
            const cellContents = r.cells.map(c => {
                const innerText = c.studentCards.map(s =>
                    `${s.hocVienTen} (${s.chuyenNganh}) - GV: ${s.gvlsTen}${s.nghiDateStr ? ` [${s.nghiDateStr}]` : ''}${s.hasConflict ? ' [Xung đột]' : ''}`
                ).join('\r\n');
                return `"${innerText}"`;
            });
            csv += cellContents.join(';') + "\n";
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', 'Ma_Tran_Lich_Lam_Sang.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Đã xuất tệp ma trận lịch lâm sàng (.csv)");
    };

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '250px' }}>
                <div className="spinner-border text-primary spinner-border-sm mr-2" role="status"></div>
                <span className="text-muted font-weight-bold">Đang tải ma trận...</span>
            </div>
        );
    }

    const weeks = matrixData?.weeks || [];
    const rawRows = matrixData?.rows || [];
    const summary = matrixData?.summary;
    const conflicts = matrixData?.detailedConflicts || [];

    const donVis = Array.isArray(donVisData) ? donVisData : donVisData?.data || [];
    const nganhs = Array.isArray(nganhsData) ? nganhsData : nganhsData?.data || [];

    const hasConflicts = conflicts.length > 0;

    // --- TRANSFORM DATA DYNAMICALLY BASED ON matrixViewMode ---
    let displayRows = [];
    let firstColumnHeader = "Khoa / Phòng";

    if (matrixViewMode === 'department') {
        displayRows = rawRows.map(r => ({
            ...r,
            id: r.donViId,
            name: r.donViTen,
            subText: ''
        }));
        firstColumnHeader = "Khoa / Phòng";
    }
    else if (matrixViewMode === 'student') {
        firstColumnHeader = "Học viên lâm sàng";
        // 1. Get all unique students from the matrix
        const studentMap = {};
        rawRows.forEach(row => {
            row.cells.forEach((cell, cellIdx) => {
                (cell.studentCards || []).forEach(card => {
                    if (card.hocVienTen) {
                        if (!studentMap[card.hocVienTen]) {
                            studentMap[card.hocVienTen] = {
                                name: card.hocVienTen,
                                major: card.chuyenNganh,
                                cells: Array.from({ length: weeks.length }, () => [])
                            };
                        }
                        studentMap[card.hocVienTen].cells[cellIdx].push({
                            id: card.id,
                            donViTen: row.donViTen,
                            gvlsTen: card.gvlsTen,
                            chuyenNganh: card.chuyenNganh,
                            nghiDateStr: card.nghiDateStr,
                            hasConflict: card.hasConflict,
                            laQuaTuBaoCao: card.laQuaTuBaoCao
                        });
                    }
                });
            });
        });

        displayRows = Object.values(studentMap)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(std => ({
                id: std.name,
                name: std.name,
                subText: std.major,
                cells: std.cells.map(cCards => ({ studentCards: cCards }))
            }));
    }
    else if (matrixViewMode === 'instructor') {
        firstColumnHeader = "Giảng viên hướng dẫn";
        // 1. Get all unique instructors
        const instructorMap = {};
        rawRows.forEach(row => {
            row.cells.forEach((cell, cellIdx) => {
                (cell.studentCards || []).forEach(card => {
                    const instructors = (card.gvlsTen || 'Chưa phân công').split(',').map(s => s.trim());
                    instructors.forEach(insName => {
                        if (!instructorMap[insName]) {
                            instructorMap[insName] = {
                                name: insName,
                                cells: Array.from({ length: weeks.length }, () => [])
                            };
                        }
                        // To avoid duplicate students on same instructor cell
                        const alreadyExists = instructorMap[insName].cells[cellIdx].some(x => x.hocVienTen === card.hocVienTen);
                        if (!alreadyExists) {
                            instructorMap[insName].cells[cellIdx].push({
                                id: card.id,
                                hocVienTen: card.hocVienTen,
                                chuyenNganh: card.chuyenNganh,
                                donViTen: row.donViTen,
                                nghiDateStr: card.nghiDateStr,
                                hasConflict: card.hasConflict,
                                laQuaTuBaoCao: card.laQuaTuBaoCao
                            });
                        }
                    });
                });
            });
        });

        displayRows = Object.values(instructorMap)
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(ins => ({
                id: ins.name,
                name: ins.name,
                subText: ins.name === 'Chưa phân công' ? 'Lớp lâm sàng' : 'GVLS',
                cells: ins.cells.map(cCards => ({ studentCards: cCards }))
            }));
    }

    return (
        <div className="p-0">
            <style>{`
                .flat-kpi-card {
                    background: #ffffff;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 12px 16px;
                }
                .flat-kpi-card.conflict {
                    border-left: 4px solid #ef4444;
                    background: #fef2f2;
                }
                .flat-matrix-table-container {
                    border: 1px solid #cbd5e1;
                    border-radius: 12px;
                    overflow: hidden;
                    background: #ffffff;
                }
                .flat-matrix-table-container::-webkit-scrollbar {
                    width: 6px;
                    height: 6px;
                }
                .flat-matrix-table-container::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                .flat-matrix-table {
                    border-collapse: separate;
                    border-spacing: 0;
                }
                 .flat-matrix-table th {
                    position: sticky;
                    top: 0;
                    background: #f8fafc;
                    color: #475569;
                    font-weight: bold;
                    border-bottom: 2px solid #cbd5e1;
                    padding: 8px 12px;
                    text-align: center;
                    font-size: 0.8rem;
                    min-width: 150px;
                    z-index: 12;
                }
                .flat-matrix-table td {
                    border: 1px solid #e2e8f0;
                    padding: 8px;
                    min-width: 150px;
                    vertical-align: top;
                    font-size: 0.8rem;
                }
                .flat-matrix-table th:first-child, .flat-matrix-table td:first-child {
                    position: sticky;
                    left: 0;
                    background: #f8fafc;
                    z-index: 14;
                    font-weight: bold;
                    border-right: 2px solid #cbd5e1;
                    min-width: 170px;
                    text-align: left;
                }
                .flat-matrix-table th:first-child {
                    z-index: 16;
                    top: 0;
                }
                .flat-capsule-card {
                    background: #f1f5f9;
                    border: 1px solid #cbd5e1;
                    border-radius: 8px;
                    padding: 6px;
                    margin-bottom: 6px;
                }
                .flat-capsule-card.conflict {
                    border: 1.5px solid #ef4444;
                    background: #fef2f2;
                }
                .flat-conflict-panel {
                    background: #fef2f2;
                    border: 1px solid #fca5a5;
                    border-radius: 12px;
                    padding: 16px;
                }
                .flat-conflict-alert {
                    border-left: 3px solid #ef4444;
                    background: #ffffff;
                    border-radius: 6px;
                    padding: 8px;
                    margin-bottom: 8px;
                    font-size: 0.75rem;
                    color: #991b1b;
                    font-weight: 500;
                    line-height: 1.4;
                }
            `}</style>

            {/* View Mode Switching Toolbar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="btn-group shadow-xs" style={{ borderRadius: '8px', overflow: 'hidden' }}>
                    <Button
                        variant={matrixViewMode === 'department' ? 'teal' : 'outline-teal'}
                        size="sm"
                        style={{
                            backgroundColor: matrixViewMode === 'department' ? '#0d9488' : 'transparent',
                            borderColor: '#0d9488',
                            color: matrixViewMode === 'department' ? '#ffffff' : '#0d9488',
                            fontWeight: 'bold',
                            padding: '8px 16px'
                        }}
                        onClick={() => setMatrixViewMode('department')}
                    >
                        🏢 Theo Khoa
                    </Button>
                    <Button
                        variant={matrixViewMode === 'student' ? 'teal' : 'outline-teal'}
                        size="sm"
                        style={{
                            backgroundColor: matrixViewMode === 'student' ? '#0d9488' : 'transparent',
                            borderColor: '#0d9488',
                            color: matrixViewMode === 'student' ? '#ffffff' : '#0d9488',
                            fontWeight: 'bold',
                            padding: '8px 16px'
                        }}
                        onClick={() => setMatrixViewMode('student')}
                    >
                        🎓 Theo Học Viên
                    </Button>
                    <Button
                        variant={matrixViewMode === 'instructor' ? 'teal' : 'outline-teal'}
                        size="sm"
                        style={{
                            backgroundColor: matrixViewMode === 'instructor' ? '#0d9488' : 'transparent',
                            borderColor: '#0d9488',
                            color: matrixViewMode === 'instructor' ? '#ffffff' : '#0d9488',
                            fontWeight: 'bold',
                            padding: '8px 16px'
                        }}
                        onClick={() => setMatrixViewMode('instructor')}
                    >
                        👨‍🏫 Theo Giảng Viên
                    </Button>
                </div>

                <div className="text-muted small">
                    Chế độ xem ma trận: <strong className="text-teal" style={{ color: '#0d9488' }}>
                        {matrixViewMode === 'department' ? 'Theo Khoa / Phòng' : matrixViewMode === 'student' ? 'Theo Học Viên' : 'Theo Giảng Viên'}
                    </strong>
                </div>
            </div>

            {/* Filter toolbar */}
            <Card className="p-3 mb-3 border-0 bg-light" style={{ borderRadius: '12px' }}>
                <Row className="align-items-center">
                    <Col md={3} className="mb-2 mb-md-0">
                        <Form.Control
                            as="select"
                            size="sm"
                            value={donViId}
                            onChange={(e) => setDonViId(e.target.value)}
                            style={{ borderRadius: '8px', height: '36px' }}
                        >
                            <option value="">Tất cả khoa ({donVis.length})</option>
                            {donVis.map(d => (
                                <option key={d.id || d.Id} value={d.id || d.Id}>{d.tenDonVi || d.TenDonVi}</option>
                            ))}
                        </Form.Control>
                    </Col>
                    <Col md={3} className="mb-2 mb-md-0">
                        <Form.Control
                            as="select"
                            size="sm"
                            value={nganhId}
                            onChange={(e) => setNganhId(e.target.value)}
                            style={{ borderRadius: '8px', height: '36px' }}
                        >
                            <option value="">Tất cả chương trình ({nganhs.length})</option>
                            {nganhs.map(n => (
                                <option key={n.id || n.Id} value={n.id || n.Id}>{n.tenNganh || n.TenNganh}</option>
                            ))}
                        </Form.Control>
                    </Col>
                    <Col md={4} className="mb-2 mb-md-0">
                        <Form.Control
                            type="text"
                            placeholder="Tìm kiếm học viên hoặc giảng viên..."
                            size="sm"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ borderRadius: '8px', height: '36px' }}
                        />
                    </Col>
                    <Col md={2} className="text-md-right">
                        <Button
                            variant="primary"
                            size="sm"
                            className="w-100 shadow-sm"
                            style={{ borderRadius: '8px', height: '36px' }}
                            onClick={handleExportMatrixCsv}
                        >
                            <i className="fas fa-file-excel mr-1"></i> Xuất Excel
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Metrics cards */}
            {summary && (
                <Row className="mb-3">
                    <Col xs={6} md={3} className="mb-2">
                        <div className="flat-kpi-card">
                            <div className="text-muted small font-weight-bold">BSNT HOẠT ĐỘNG</div>
                            <h3 className="font-weight-bold text-dark mb-0 mt-1">{summary.activeStudentsCount}</h3>
                        </div>
                    </Col>
                    <Col xs={6} md={3} className="mb-2">
                        <div className="flat-kpi-card">
                            <div className="text-muted small font-weight-bold">GVLS GIẢNG DẠY</div>
                            <h3 className="font-weight-bold text-dark mb-0 mt-1">{summary.activeTeachersCount}</h3>
                        </div>
                    </Col>
                    <Col xs={6} md={3} className="mb-2">
                        <div className="flat-kpi-card">
                            <div className="text-muted small font-weight-bold">KHOA LÂM SÀNG</div>
                            <h3 className="font-weight-bold text-dark mb-0 mt-1">{summary.activeDeptsCount}</h3>
                        </div>
                    </Col>
                    <Col xs={6} md={3} className="mb-2">
                        <div className={`flat-kpi-card ${summary.conflictsCount > 0 ? 'conflict' : ''}`}>
                            <div className="text-muted small font-weight-bold">CẢNH BÁO XUNG ĐỘT</div>
                            <h3 className={`font-weight-bold mb-0 mt-1 ${summary.conflictsCount > 0 ? 'text-danger' : 'text-dark'}`}>
                                {summary.conflictsCount}
                            </h3>
                        </div>
                    </Col>
                </Row>
            )}

            {/* Side-by-side Matrix Grid & Conflicts List */}
            {displayRows.length === 0 ? (
                <Card className="text-center py-5 border-0 bg-light" style={{ borderRadius: '12px' }}>
                    <Card.Body className="text-muted">
                        <p className="mb-0">Không tìm thấy dữ liệu ma trận đào tạo lâm sàng nào.</p>
                    </Card.Body>
                </Card>
            ) : (
                <Row>
                    {/* Bảng Ma trận (9 cột nếu có xung đột, 12 cột nếu không có) */}
                    <Col lg={hasConflicts ? 9 : 12} className="mb-3">
                        <div className="flat-matrix-table-container">
                            <div className="table-responsive" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                                <Table className="flat-matrix-table mb-0">
                                    <thead>
                                        <tr>
                                            <th>{firstColumnHeader}</th>
                                            {weeks.map((w, idx) => (
                                                <th key={idx}>
                                                    <div>{w.rangeStr}</div>
                                                    <div className="text-muted font-weight-normal small mt-1">{w.label}</div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayRows.map((row, rIdx) => (
                                            <tr key={row.id || rIdx}>
                                                <td className="sticky-left">
                                                    <div className="font-weight-bold text-dark">{row.name}</div>
                                                    {row.subText && (
                                                        <div className="text-muted extra-small font-weight-normal" style={{ fontSize: '0.7rem' }}>
                                                            {row.subText}
                                                        </div>
                                                    )}
                                                </td>
                                                {row.cells.map((cell, cIdx) => (
                                                    <td key={cIdx}>
                                                        {cell.studentCards && cell.studentCards.map((card, cardIdx) => {
                                                            // Customize rendering card based on mode
                                                            if (matrixViewMode === 'department') {
                                                                return (
                                                                    <div key={card.id || cardIdx} className={`flat-capsule-card ${card.hasConflict ? 'conflict' : ''}`}>
                                                                        <div className="font-weight-bold text-dark mb-1">
                                                                            <i className="fas fa-user-md mr-1 text-teal" style={{ color: '#0d9488' }}></i> {card.gvlsTen || 'Chưa phân công'}
                                                                        </div>
                                                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            <span>
                                                                                {card.laQuaTuBaoCao && (
                                                                                    <i className="fas fa-bullhorn text-warning mr-1" title="Lịch tự báo cáo đổi giảng viên"></i>
                                                                                )}
                                                                                HV: <strong>{card.hocVienTen}</strong>
                                                                            </span>
                                                                            <Badge bg="secondary" className="text-dark small" style={{ fontSize: '0.6rem', backgroundColor: '#e2e8f0' }}>
                                                                                {card.chuyenNganh}
                                                                            </Badge>
                                                                        </div>
                                                                        {card.nghiDateStr && (
                                                                            <div className="text-danger font-weight-bold mt-1" style={{ fontSize: '0.75rem' }}>
                                                                                Nghỉ phép ({card.nghiDateStr.replace('Nghỉ ', '')})
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                            else if (matrixViewMode === 'student') {
                                                                return (
                                                                    <div key={card.id || cardIdx} className={`flat-capsule-card ${card.hasConflict ? 'conflict' : ''}`}>
                                                                        <div className="font-weight-bold text-dark mb-1">
                                                                            <i className="fas fa-user-md mr-1 text-teal" style={{ color: '#0d9488' }}></i>GV: {card.gvlsTen || 'Chưa phân công'}
                                                                        </div>
                                                                        <div className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                                                            <i className="fas fa-hospital mr-1 text-muted"></i> {card.donViTen}
                                                                        </div>
                                                                        {card.laQuaTuBaoCao && (
                                                                            <div className="text-warning small" style={{ fontSize: '0.7rem' }}>
                                                                                <i className="fas fa-bullhorn mr-1"></i> Tự báo cáo
                                                                            </div>
                                                                        )}
                                                                        {card.nghiDateStr && (
                                                                            <div className="text-danger font-weight-bold mt-1" style={{ fontSize: '0.75rem' }}>
                                                                                Nghỉ phép ({card.nghiDateStr.replace('Nghỉ ', '')})
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                            else { // instructor mode
                                                                return (
                                                                    <div key={card.id || cardIdx} className={`flat-capsule-card ${card.hasConflict ? 'conflict' : ''}`}>
                                                                        <div className="font-weight-bold text-dark mb-1">
                                                                            <i className="fas fa-user-graduate mr-1 text-primary"></i> {card.hocVienTen}
                                                                        </div>
                                                                        <div className="d-flex justify-content-between align-items-center mb-1 text-muted" style={{ fontSize: '0.75rem' }}>
                                                                            <span>Khoa: <strong>{card.donViTen}</strong></span>
                                                                            <Badge bg="secondary" className="text-dark small" style={{ fontSize: '0.6rem', backgroundColor: '#e2e8f0' }}>
                                                                                {card.chuyenNganh}
                                                                            </Badge>
                                                                        </div>
                                                                        {card.laQuaTuBaoCao && (
                                                                            <div className="text-warning mb-1" style={{ fontSize: '0.7rem' }}>
                                                                                <i className="fas fa-bullhorn mr-1"></i> Tự báo cáo
                                                                            </div>
                                                                        )}
                                                                        {card.nghiDateStr && (
                                                                            <div className="text-danger font-weight-bold mt-1" style={{ fontSize: '0.75rem' }}>
                                                                                Nghỉ phép ({card.nghiDateStr.replace('Nghỉ ', '')})
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            }
                                                        })}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    </Col>

                    {/* Danh sách cảnh báo xung đột (3 cột hiển thị song song bên phải) */}
                    {hasConflicts && (
                        <Col lg={3} className="mb-3">
                            <div className="flat-conflict-panel h-100" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                                <h6 className="font-weight-bold text-danger mb-3">
                                    <i className="fas fa-exclamation-triangle mr-1"></i> Xung đột GVLS ({conflicts.length})
                                </h6>
                                {conflicts.map((msg, idx) => (
                                    <div key={idx} className="flat-conflict-alert">
                                        {msg}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    )}
                </Row>
            )}
        </div>
    );
};

export default AdminMatrixView;
